// src/ui/controls/OnFloorDropButton.tsx — P3 D-25 / UI-SPEC §OnFloorDropButton.
//
// Dedicated drop-to-floor button for hider characters with the 'on-floor'
// ability (D-25). Tap fires `localInputController.requestDropToFloor()`; the
// sim creates a synthetic floor hiding-spot at the player's current position.
//
// WARNING-15 tier-isolation: this component MUST NOT import PROFILES. The
// player's ability tokens arrive on the sim-tick payload
// (e.playerAbilities) having been resolved inside GameScene at emit time.
// TODO(P5 CHAR-07): admin-CMS loading swaps the resolution source from the
// hardcoded PROFILES record to a Cloudflare-KV fetch, without changing this
// component — the sim-tick payload shape is stable.
//
// Source: UI-SPEC §OnFloorDropButton; PATTERNS.md §HUD controls (closest
// analog is TorchButton's one-shot-flag + renderBus subscription pattern).

import {
  useCallback,
  useEffect,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { renderBus } from '../../render/bus';
import { localInputController } from '../../sim/controllers/registry';

import './on-floor-drop-button.css';

export function OnFloorDropButton(): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    return renderBus.on('sim-tick', (e) => {
      // Visibility gate: hider + ability-gated + free. Abilities come from
      // the sim-tick payload — WARNING-15 tier isolation.
      const canDrop =
        e.playerRole === 'hider' &&
        Array.isArray(e.playerAbilities) &&
        e.playerAbilities.includes('on-floor') &&
        e.playerStatus === 'free';
      setVisible(canDrop);
    });
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      localInputController.requestDropToFloor();
    },
    [],
  );

  if (!visible) return null;

  return (
    <button
      type="button"
      className="on-floor-drop-button"
      data-testid="on-floor-drop-button"
      aria-label="Drop to floor"
      onPointerDown={onPointerDown}
      // Inline style mirrors on-floor-drop-button.css (PATTERNS Pattern E).
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        right: 'calc(env(safe-area-inset-right) + 16px + 8px)',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        touchAction: 'none',
        pointerEvents: 'auto',
        zIndex: 60,
      }}
    >
      {/* Inline pictograph SVG — lying-down stick figure (horizontal line +
       *  small circle head) to differentiate from the bush/tree of the
       *  HideExitButton at small sizes (UI-SPEC §Glyph pictographs). */}
      <svg
        viewBox="0 0 32 32"
        width={32}
        height={32}
        aria-hidden="true"
        focusable="false"
      >
        <circle cx={10} cy={20} r={3} fill="currentColor" />
        <line
          x1={12}
          y1={20}
          x2={26}
          y2={20}
          stroke="currentColor"
          strokeWidth={2}
        />
      </svg>
    </button>
  );
}
